import { Theme, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import homeIcon from '../../assets/car-icon.svg';
import logoutIcon from '../../assets/logout-grey.svg';
import mobiversLogo from '../../assets/mobivers-logo-red.svg';
import switchIcon from '../../assets/switch-icon-grey.svg';
import { ROUTE_HOME, ROUTE_LOGOUT, ROUTE_THANKYOU } from '../../lib/constants';
import { useUniversalTracking } from '../../lib/tracking/universalTracking';
import { logOutUser, switchToBackoffice } from '../../redux/actions/mobivers';
import { colors } from '../../theme/theme';
import { PropAlignment } from '../../types/materialUi';
import { AppState, CustomizingState, UserState } from '../../types/redux';
import { getEnvironmentTitle } from '../../utils/environment';
import { GenericModal } from '../GenericModal/GenericModal';
import { deleteCookie } from 'utils/cookie';
import {getUrlDepandOnCustomization} from "../../redux/actions/customizing";

interface Props extends StateProps, DispatchProps, RouteComponentProps {}

const HEADER_HEIGHT = 56;

const useStyles = makeStyles((theme: Theme) => ({
	main: {
		height: HEADER_HEIGHT,
		boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingLeft: theme.spacing(3),
		paddingRight: 26,
		backgroundColor: colors.white,
	},
	homeButton: {
		flex: '0 0 64px',
		height: 56,
		padding: '12px 14px',
		borderRight: '1px solid #999999',
		marginRight: theme.spacing(2),
		marginLeft: -theme.spacing(3),
		cursor: 'pointer',
		boxSizing: 'border-box',
	},
	homeIcon: {
		width: '100%',
		opacity: 0.75,
		maxWidth: 140,
		maxHeight: HEADER_HEIGHT - 8,
	},
	logo: {
		marginRight: 20,
		marginLeft: 5,
		maxHeight: HEADER_HEIGHT - 8,
		maxWidth: 140,
		height: '100%',
	},
	headerGroup: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
	},
	logoutButton: {
		width: 30,
		marginLeft: 20,
		padding: '14px 0',
		cursor: 'pointer',
		boxSizing: 'border-box',
	},
	logoutButtonIcon: {
		width: '100%',
	},
	userName: {
		color: colors.grayA80,
	},
}));
const InternalHeader: FunctionComponent<Props> = (props) => {

	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const [
		showSwitchToBackofficeModal,
		setShowSwitchToBackofficeModal,
	] = useState(false);
	const { trackEvent } = useUniversalTracking();
	const { t } = useTranslation();
	const classes = useStyles();
	const customizing: CustomizingState = useSelector<AppState, CustomizingState>( (state) => state.customizing );

	const handleLogout = () => {
		trackEvent({
			category: 'header-logout',
			action: 'click-event',
			label: 'Logout button clicked',
		});
		props.logOutUser();
		props.history.push(`/${ROUTE_LOGOUT}`);
	};

	const handleSwitchToBackoffice = () => {
		trackEvent({
			category: 'header-switch-to-backoffice',
			action: 'click-event',
			label: 'switch to backoffice button clicked',
		});
		setShowSwitchToBackofficeModal(false);
		props.switchToBackoffice();
	};

	const goHome = () => {
		trackEvent({
			category: 'header-home',
			action: 'click-event',
			label: 'Home button clicked',
		});
		props.history.push(`/${ROUTE_HOME}`);
	};

	const shouldDisplayHomeButton = (path: string) =>
		!props.location.pathname.includes(ROUTE_HOME) &&
		!props.location.pathname.includes(ROUTE_THANKYOU);

	const shouldDisplayBackofficeButton = (path: string) =>
		!!getUrlDepandOnCustomization(customizing, process.env.REACT_APP_BACKOFFICE_SSO_URL) &&
		props.location.pathname.includes(ROUTE_HOME);

	const confirmLogoutModal = () => (
		<GenericModal
			showModal={showLogoutModal}
			okButton={{
				handleFn: handleLogout,
				text: t('logout.OK' as const),
			}}
			cancelButton={{
				handleFn: () => setShowLogoutModal(false),
			}}
		>
			<div>{t('logout.message' as const)}</div>
		</GenericModal>
	);

	const confirmSwitchToBackofficeModal = () => (
		<GenericModal
			showModal={showSwitchToBackofficeModal}
			okButton={{
				handleFn: handleSwitchToBackoffice,
				text: t('switchToBackoffice.OK'),
			}}
			cancelButton={{
				handleFn: () => setShowSwitchToBackofficeModal(false),
				text: t('switchToBackoffice.cancel'),
			}}
		>
			<div>{t('switchToBackoffice.message')}</div>
		</GenericModal>
	);

	return (
		<div className={classes.main}>
			<div className={classes.headerGroup}>
				{shouldDisplayHomeButton(props.location.pathname) && (
					<Tooltip
						title={t('tooltip.backToHome') ?? ''}
						enterDelay={500}
						leaveDelay={200}
					>
						<div
							className={`header_home_icon ${classes.homeButton}`}
							onClick={() => goHome()}
							role="button"
						>
							<img
								src={homeIcon}
								alt="header_home_icon"
								className={classes.homeIcon}
							/>
						</div>
					</Tooltip>
				)}
				<img
					src={customizing.logoSrc ?? mobiversLogo}
					alt="logo"
					className={classes.logo}
				/>

				<MediaQuery minWidth={670}>
					<Typography variant="body1">{getEnvironmentTitle()}</Typography>
				</MediaQuery>
			</div>
			<div className={classes.headerGroup}>
				<Typography
					variant="body1"
					align={PropAlignment.RIGHT}
					className={classes.userName}
				>
					{props.user.name}
				</Typography>
				{shouldDisplayBackofficeButton(props.location.pathname) && (
					<Tooltip
						title={t('tooltip.switchToBackoffice') ?? ''}
						enterDelay={500}
						leaveDelay={200}
					>
						<div
							className={classes.logoutButton}
							onClick={() => setShowSwitchToBackofficeModal(true)}
							role="button"
						>
							<img
								src={switchIcon}
								alt="switch to backoffice"
								className={classes.logoutButtonIcon}
							/>
						</div>
					</Tooltip>
				)}
				<Tooltip
					title={t('tooltip.logout') ?? ''}
					enterDelay={500}
					leaveDelay={200}
				>
					<div
						className={`header_logout ${classes.logoutButton}`}
						onClick={() => setShowLogoutModal(true)}
						role="button"
					>
						<img
							src={logoutIcon}
							alt="logout user"
							className={classes.logoutButtonIcon}
						/>
					</div>
				</Tooltip>
			</div>
			{confirmLogoutModal()}
			{confirmSwitchToBackofficeModal()}
		</div>
	);
};

interface StateProps {
	user: UserState;
}

interface DispatchProps {
	logOutUser(): void;
	switchToBackoffice(): void;
}

const mapStateToProps = (state: AppState) => ({
	user: state.user,
});

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({
	logOutUser: () => {
		deleteCookie('dms-data');
		dispatch(logOutUser());
	},
	switchToBackoffice: () => {
		dispatch(switchToBackoffice());
	},
});

const withRouterComp = withRouter(InternalHeader);
export const Header = connect(
	mapStateToProps,
	mapDispatchToProps
)(withRouterComp);
